import {
  Badge,
  Card,
  Divider,
  Flex,
  Group,
  Progress,
  SemiCircleProgress,
  Stack,
  Text,
  Title,
  rem,
} from "@mantine/core";
import { IconAlertCircle, IconAlertTriangle, IconCircleCheck } from "@tabler/icons-react";

import React from "react";

export interface DyslexiaProbabilityCardProps {
  probability: number;
  title?: string;
  indicatorLabel?: string;
}

const DyslexiaProbabilityCard: React.FC<DyslexiaProbabilityCardProps> = (props) => {
  const {
    probability,
    title = "Dyslexia Risk Assessment",
    indicatorLabel = "dyslexia-related",
  } = props;
  const lowRiskThreshold = 40;
  const moderateRiskThreshold = 70;
  const riskIndicators = [
    {
      name: "High Regression Rate",
      description: "Normal backward eye movement pattern",
      detected: false,
    },
    {
      name: "Prolonged Fixations",
      description: "Normal fixation duration",
      detected: false,
    },
    {
      name: "Reading Speed",
      description: "Age-appropriate reading speed",
      detected: false,
    },
    {
      name: "Irregular Saccades",
      description: "Consistent eye movement patterns",
      detected: false,
    },
  ];

  const RiskBadge = ({ prob }: { prob: number }) => {
    const color =
      prob > moderateRiskThreshold ? "red" : prob > lowRiskThreshold ? "yellow" : "green";
    const text =
      prob > moderateRiskThreshold
        ? "Elevated Risk"
        : prob > lowRiskThreshold
          ? "Moderate Risk"
          : "Low Risk";
    return (
      <Badge size="lg" variant="outline" color={color}>
        {text}
      </Badge>
    );
  };

  const RiskLegend = () => {
    return (
      <Progress.Root size="xl" w={"100%"} h={rem(25)}>
        <Progress.Section value={lowRiskThreshold} color="green">
          <Progress.Label>{`0-${lowRiskThreshold}%`}</Progress.Label>
        </Progress.Section>
        <Progress.Section value={moderateRiskThreshold - lowRiskThreshold} color="yellow">
          <Progress.Label>{`${lowRiskThreshold}-${moderateRiskThreshold}%`}</Progress.Label>
        </Progress.Section>
        <Progress.Section value={100 - moderateRiskThreshold} color="red">
          <Progress.Label>{`${moderateRiskThreshold}-100%`}</Progress.Label>
        </Progress.Section>
      </Progress.Root>
    );
  };

  const RiskPercent = ({ prob }) => {
    return (
      <SemiCircleProgress
        fillDirection="left-to-right"
        orientation="up"
        filledSegmentColor="green"
        size={200}
        thickness={16}
        styles={{
          filledSegment: {
            strokeLinecap: "round",
          },
          label: {
            fontSize: "1.25rem",
            fontWeight: 700,
          },
        }}
        value={prob}
        label={`${prob}%`}
      />
    );
  };

  const RiskIndicatorItem = ({
    item,
  }: {
    item: { name: string; description: string; detected: boolean };
  }) => {
    return (
      <Card style={{ backgroundColor: "var(--oculera-surface-alt)" }} p={"xs"}>
        <Flex align={"flex-start"} gap={"sm"}>
          {item.detected ? (
            <IconAlertCircle size={20} style={{ color: "orange" }} />
          ) : (
            <IconCircleCheck size={20} style={{ color: "green" }} />
          )}
          <Stack gap={0}>
            <Text fw={600}>{item.name}</Text>
            <Text c="dimmed" fz="sm">
              {item.description}
            </Text>
          </Stack>
        </Flex>
      </Card>
    );
  };

  return (
    <Card withBorder radius="md" shadow="md">
      <Group>
        <IconAlertTriangle size={25} style={{ color: "orange" }} />
        <Title order={4}>{title}</Title>
      </Group>
      <Flex justify={"space-between"} align={"center"} w={"100%"} gap={"xl"} h={"100%"}>
        <Stack justify="space-between" align="center" gap={"lg"} w={"50%"} h={"100%"}>
          <RiskPercent prob={probability} />
          <RiskBadge prob={probability} />
          <Divider size={"lg"} />
          <RiskLegend />
        </Stack>
        <Stack w={"50%"} gap={"md"}>
          <Text fw={500} fz="md">
            {`Based on eye tracking patterns, ${riskIndicators.filter((indicator) => indicator.detected).length} 
            of 4 ${indicatorLabel} indicators were detected:`}
          </Text>
          {riskIndicators.map((indicator, index) => (
            <RiskIndicatorItem key={index} item={indicator} />
          ))}
        </Stack>
      </Flex>
    </Card>
  );
};

export default DyslexiaProbabilityCard;
